/**
 * PdfPreviewModal — full-screen inline PDF viewer used by the Certificate and
 * Transcript pages. Renders a browser-native `<iframe>` over an opaque backdrop
 * with a download link and Esc/click-to-close behaviour.
 *
 * Props:
 *   open       — boolean; false hides the modal (also returns nothing so the
 *                iframe doesn't stay mounted holding onto a blob URL).
 *   url        — blob or data URL of the PDF.
 *   title      — small caption shown in the modal header (student + document).
 *   filename   — used for the Download link's `download` attribute.
 *   onClose    — invoked on Esc, close button click or backdrop click.
 */
import { useEffect } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";

export default function PdfPreviewModal({ open, url, title, filename, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !url) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900 flex flex-col"
      onClick={onClose}
      data-testid="pdf-preview-modal"
    >
      <div className="flex justify-between items-center p-3 text-white">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">Document Preview</p>
          <p className="text-sm text-white truncate">{title}</p>
        </div>
        <div className="flex gap-2">
          <a
            href={url}
            download={filename}
            onClick={(e) => e.stopPropagation()}
            className="rounded-none border border-white/30 text-white px-3 py-1.5 text-xs hover:bg-white/10 flex items-center gap-1"
            data-testid="pdf-preview-download"
          ><DownloadSimple size={14} /> Download</a>
          <button
            onClick={(e) => { e.stopPropagation(); onClose && onClose(); }}
            className="rounded-none border border-white/30 text-white px-3 py-1.5 text-xs hover:bg-white/10 flex items-center gap-1"
            data-testid="pdf-preview-close"
          ><X size={14} /> Close (Esc)</button>
        </div>
      </div>
      <div
        className="flex-1 flex items-center justify-center overflow-auto p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <iframe
          src={url}
          title={title || "PDF Preview"}
          className="w-full h-full bg-white"
          style={{ maxWidth: "1200px" }}
          data-testid="pdf-preview-iframe"
        />
      </div>
    </div>
  );
}
